import { SimulationError } from '@/services/simulate/simulate'

import { getRawErrorMessage, isAppTransportError, isWalletProviderRpcError } from './error-summary'

/**
 * Client-side transaction error classifier.
 * Classifies error messages into categories for metrics.
 */

// Keep in sync with TRANSACTION_ERROR_CATEGORIES in src/app/api/t/constants.ts.
export const TRANSACTION_ERROR_CATEGORIES = [
  'user_rejected',
  'gas_issue',
  'slippage',
  'simulation_failed',
  'transaction_reverted_onchain',
  'timeout',
  'timeout_receipt_polling',
  'timeout_rpc_request',
  'network_error',
  'rate_limit',
  'invalid_state',
  'wallet_response_error',
  'wallet_session_error',
  'safe_proposal_pending',
  'safe_wallet_expired',
  'safe_wallet_replaced',
  'unknown'
] as const

export type TransactionErrorCategory = (typeof TRANSACTION_ERROR_CATEGORIES)[number]

export const TRANSACTION_OUTCOMES = [
  'success',
  'failure',
  'user_rejected',
  'receipt_timeout_pending'
] as const

export type TransactionOutcome = (typeof TRANSACTION_OUTCOMES)[number]

const USER_REJECTION_PATTERNS = [
  'user rejected',
  'user denied',
  'user cancelled',
  'user canceled',
  'action_rejected',
  'rejected the request',
  'request rejected',
  'transaction was rejected',
  'signature denied',
  'declined',
  '4001',
  'rejected by user',
  'userrejectedrequesterror',
  'user disconnected',
  'user disapproved',
  'okx wallet reject',
  '已取消'
]

const WALLET_ERROR_PATTERNS = [
  'disconnected port',
  'extension context invalidated',
  'wallet extension',
  'extension has not been configured',
  'has not been authorized yet',
  'not been authorized by the user',
  'wkwebview api client did not respond',
  'onekeyerror',
  'switchchainnotsupportederror',
  'does not support programmatic chain switching',
  'failed to sign',
  'invalid signature',
  'signature verification failed',
  'yparityorv',
  "for 'r' in null",
  'rejected by policy',
  'ledger',
  'device is unlocked',
  'device must be opened',
  'hardware wallet',
  'trezor',
  'instead of a transaction hash'
]

const WALLET_SESSION_PATTERNS = [
  'pairing already exists',
  'no matching key',
  'proposal expired',
  'session expired',
  'request expired',
  'no connected wallet',
  'session settlement failed',
  'provider is disconnected',
  'not authorized for given chain',
  'failed to publish payload',
  'socket stalled',
  'websocket connection failed'
]

const NETWORK_NOISE_PATTERNS = [
  'connection interrupted',
  'the network connection was lost',
  'the operation was aborted',
  'aborterror'
]

const GENERIC_RPC_WRAPPER_PATTERNS = [
  'internal error was received',
  'unknown rpc error',
  'resource not available',
  'protocol is not supported'
]

const STALE_NONCE_PATTERNS = [
  'nonce too low',
  'nonce too high',
  'lower than the current nonce',
  'nonce is too low',
  'nonce has already been used',
  'invalid nonce',
  'higher than the next one',
  'exceeds the maximum allowed nonce'
]

const DETAILS_CANCELED = /details:\s*cancell?ed\.?\s*(?:version:|$)/

// viem appends the call/request args and decoded-revert args to its messages;
// their hex/number payload must never take part in pattern matching, while
// `Details:` still must.
const VIEM_ARGS_BLOCK =
  /(?:request arguments|estimate gas arguments|raw call arguments|contract call):[\s\S]*?(?=details:|version:|docs:|\n\s*\n|$)/gi

// `Error: Name(type a, (tuple t) b)` followed by an indented `(value, value)` line
const VIEM_DECODED_REVERT_BLOCK = /error:\s*[a-z_$][\w$]*\([^\n]*\)(?:\s*\([^\n]*\))?/gi

function isUserRejection(message: string, context: { fromAppTransport?: boolean }): boolean {
  return (
    USER_REJECTION_PATTERNS.some(pattern => message.includes(pattern)) ||
    (!context.fromAppTransport && DETAILS_CANCELED.test(message))
  )
}

export function classifyTransactionError(
  errorMessage: string,
  context: { fromWalletProvider?: boolean; fromAppTransport?: boolean } = {}
): TransactionErrorCategory {
  const message = errorMessage
    .toLowerCase()
    .replace(VIEM_ARGS_BLOCK, ' ')
    .replace(VIEM_DECODED_REVERT_BLOCK, ' ')

  if (isUserRejection(message, context)) {
    return 'user_rejected'
  }

  // Gas-related errors
  if (
    message.includes('insufficient funds') ||
    message.includes('gas required exceeds') ||
    message.includes('exceeds block gas limit') ||
    message.includes('exceeds max transaction gas limit') ||
    message.includes('intrinsic gas too low') ||
    message.includes('out of gas') ||
    message.includes('gas limit too') ||
    message.includes('max fee per gas') ||
    message.includes('fee cap') ||
    message.includes('amount of gas provided') ||
    message.includes('total cost (gas')
  ) {
    return 'gas_issue'
  }

  // Slippage errors
  if (
    message.includes('slippage') ||
    message.includes('price impact') ||
    message.includes('price changed') ||
    message.includes('excessive slippage')
  ) {
    return 'slippage'
  }

  // Transaction reverted on-chain (mined but failed)
  if (
    message.includes('reverted on-chain') ||
    message.includes('transaction reverted') ||
    message.includes('transaction failed: 0x')
  ) {
    return 'transaction_reverted_onchain'
  }

  // Simulation failures
  if (
    message.includes('revert') ||
    message.includes('execution reverted') ||
    message.includes('call exception') ||
    message.includes('simulat')
  ) {
    return 'simulation_failed'
  }

  if (message.includes('safe transaction expired')) return 'safe_wallet_expired'
  if (message.includes('safe transaction replaced')) return 'safe_wallet_replaced'

  // Timeout errors — distinguish receipt polling from RPC request timeouts
  if (
    message.includes('waitfortransactionreceipt') ||
    message.includes('waiting for transaction') ||
    message.includes('to be confirmed')
  ) {
    return 'timeout_receipt_polling'
  }

  if (
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('etimedout') ||
    message.includes('request took too long')
  ) {
    return 'timeout_rpc_request'
  }

  // Rate limiting errors
  if (
    message.includes('rate limit') ||
    message.includes('being rate limited') ||
    message.includes('too many requests') ||
    message.includes('429') ||
    message.includes('exceeds defined limit')
  ) {
    return 'rate_limit'
  }

  // Explicit nonce/fee details win over the generic RPC wrapper markers below
  // (viem wraps node errors as "An internal error was received. ... Details: <node message>")
  if (message.includes('replacement transaction underpriced')) {
    return 'gas_issue'
  }
  if (STALE_NONCE_PATTERNS.some(pattern => message.includes(pattern))) {
    return 'invalid_state'
  }

  if (
    !context.fromAppTransport &&
    WALLET_SESSION_PATTERNS.some(pattern => message.includes(pattern))
  ) {
    return 'wallet_session_error'
  }

  // Wallet-specific errors
  if (WALLET_ERROR_PATTERNS.some(pattern => message.includes(pattern))) {
    return 'wallet_response_error'
  }

  // Network/connection/RPC provider errors
  if (
    message.includes('failed to fetch') ||
    message.includes('load failed') ||
    message.includes('networkerror') ||
    message.includes('network error') ||
    message.includes('network request failed') ||
    message.includes('econnrefused') ||
    message.includes('enotfound') ||
    message.includes('econnreset') ||
    message.includes('http request failed') ||
    GENERIC_RPC_WRAPPER_PATTERNS.some(pattern => message.includes(pattern)) ||
    NETWORK_NOISE_PATTERNS.some(pattern => message.includes(pattern)) ||
    (context.fromAppTransport && WALLET_SESSION_PATTERNS.some(pattern => message.includes(pattern)))
  ) {
    if (
      context.fromWalletProvider &&
      GENERIC_RPC_WRAPPER_PATTERNS.some(pattern => message.includes(pattern))
    ) {
      return 'wallet_response_error'
    }

    return 'network_error'
  }

  // Unexpected response type from wallet
  if (message.includes('unexpected response type')) {
    return 'wallet_response_error'
  }

  // App-specific errors
  if (message.includes('executerequirement failed') || message.includes('executebundle failed')) {
    return 'invalid_state'
  }

  // Chain/account/nonce state errors
  if (
    message.includes('chain id mismatch') ||
    message.includes('does not match the target chain') ||
    message.includes('account mismatch') ||
    message.includes('address is missing') ||
    message.includes('invalid argument') ||
    message.includes('invalid parameters') ||
    message.includes('missing or invalid') ||
    message.includes('invalid signature') ||
    message.includes('hex string has length')
  ) {
    return 'invalid_state'
  }

  return 'unknown'
}

interface ClassifyResult {
  outcome: TransactionOutcome
  errorCategory: TransactionErrorCategory | undefined
}

/**
 * Type-aware variant of `classifyTransactionError`: an actual `SimulationError`
 * is always `simulation_failed` regardless of its (copy-evolving) message.
 */
export function classifyTransactionErrorFrom(error: unknown): TransactionErrorCategory {
  if (error instanceof SimulationError) return 'simulation_failed'
  if (error instanceof Error && error.name === 'SafeProposalPendingError')
    return 'safe_proposal_pending'

  return classifyTransactionError(getRawErrorMessage(error), {
    fromWalletProvider: isWalletProviderRpcError(error),
    fromAppTransport: isAppTransportError(error)
  })
}

/**
 * Classifies an error and determines the final outcome.
 * User rejections are transformed to a separate outcome.
 */
export function classifyAndTransformOutcome(
  outcome: 'success' | 'failure' | 'receipt_timeout_pending',
  error?: unknown
): ClassifyResult {
  if (outcome !== 'failure' || error === undefined) {
    return { outcome, errorCategory: undefined }
  }

  const errorCategory = classifyTransactionErrorFrom(error)

  // Transform outcome to user_rejected if detected
  if (errorCategory === 'user_rejected') {
    return { outcome: 'user_rejected', errorCategory }
  }

  return { outcome: 'failure', errorCategory }
}
